import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, download, errMsg, fmtNum } from "@/lib/api";

const ROWS = [
  ["rows", "Rows in file"], ["matched", "Matched (material + plant)"], ["changed", "Changed (old → new)"], ["newly_filled", "Newly filled (material had no code)"],
  ["same", "Same as now"], ["blank_in_sap", "Blank in SAP (left unchanged)"], ["invalid", "Invalid (not a valid HS code)"], ["not_in_system", "Not in the system (not created)"],
  ["corrections_kept", "App corrections kept"], ["overrides_reset", "Decisions reset (code changed)"], ["not_required_left", "Not required (left alone)"], ["not_in_system_skipped", "Deleted by a user (left alone)"],
];
const DASH = [["missing_code", "Missing code"], ["check", "Check"], ["in_scope", "In scope"]];

const Counts = ({ s }) => (
  <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm" data-testid="codes-preview-counts">
    {ROWS.map(([k, l]) => (
      <div key={k} className="flex justify-between border-b border-slate-100 py-1"><span className="text-slate-600">{l}</span><span className="font-mono tabular-nums font-semibold" data-testid={`codes-preview-${k}`}>{fmtNum(s[k] || 0)}</span></div>
    ))}
  </div>
);

const Scope = ({ s }) => (
  <div className="mt-4 grid gap-4 sm:grid-cols-2">
    <div data-testid="codes-preview-scope-changes">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Scope changes ({fmtNum(s.scope_changes_total || 0)})</p>
      {Object.entries(s.scope_changes || {}).map(([k, v]) => <p key={k} className="flex justify-between text-sm"><span>{k}</span><span className="font-mono">{fmtNum(v)}</span></p>)}
      {!s.scope_changes_total && <p className="text-sm text-slate-400">None</p>}
    </div>
    <div data-testid="codes-preview-dashboard">
      <p className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Dashboard (in stock): before → after</p>
      {DASH.map(([k, l]) => <p key={k} className="flex justify-between text-sm" data-testid={`codes-preview-dash-${k}`}><span>{l}</span><span className="font-mono">{fmtNum(s.dashboard_before?.[k])} → {fmtNum(s.dashboard_after?.[k])}</span></p>)}
    </div>
  </div>
);

export const SapCodesPreview = ({ rec, onClose, onApplied }) => {
  const [busy, setBusy] = useState(false);
  const s = rec?.preview || {};
  const apply = async () => {
    setBusy(true);
    try { await api.post(`/uploads/${rec.id}/apply-codes`); toast.success("Applying SAP commodity codes in the background"); onApplied(rec.id); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!rec} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="codes-preview-dialog">
        <DialogHeader><DialogTitle>SAP commodity codes preview · {rec?.filename}</DialogTitle></DialogHeader>
        <p className="text-sm text-slate-600">Nothing has changed yet. Matching is on material + plant. Blank SAP codes never overwrite a code, app corrections are kept, and no materials are created.</p>
        <Counts s={s} />
        <Scope s={s} />
        <div className="mt-2 flex justify-end gap-2">
          <Button variant="outline" onClick={() => download(`/uploads/${rec.id}/codes-preview.xlsx`, `sap_codes_preview_${rec.filename}.xlsx`)} data-testid="codes-preview-xlsx"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Download preview Excel</Button>
          {rec?.status === "preview_ready" && <Button onClick={apply} disabled={busy || !(s.changed + s.newly_filled)} data-testid="codes-preview-apply">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Apply {fmtNum((s.changed || 0) + (s.newly_filled || 0))} changes</Button>}
        </div>
      </DialogContent>
    </Dialog>
  );
};
